// lib/leasing/chat-sessions.ts
import {
  type ActiveEntityFocus,
  type ChatSession,
  type ChatSessionContextAnchor,
  type ChatSubjectFocusEntry,
  type CollaborationMode,
  type ContextAnchor,
  type Message,
  type WorkingMemory,
} from '#types/types';
import { readListCreativeContext } from '#lib/list/listCreativeContext';
import { migrateLegacySessionProjectId } from '#lib/leasing/chat-session-project-focus';
import { messageContentToText, normalizeSnippetText } from '#lib/leasing/timeline-events';
import {
  buildSubjectFocusHistoryKey,
  normalizeSubjectFocus,
  toSubjectFocusHistoryEntry,
} from '#lib/leasing/session-focus';

export type DealerChatSessionState = ChatSession & {
  updatedAt: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isContextAnchor = (value: unknown): value is ContextAnchor => {
  if (!isRecord(value)) return false;
  return (
    typeof value.entityType === 'string' &&
    typeof value.entityId === 'string' &&
    typeof value.mass === 'number'
  );
};

const toIsoString = (value?: string): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  return new Date().toISOString();
};

const MAX_CONTEXT_SUMMARY_SIZE = 12;
const MAX_SUBJECT_HISTORY_SIZE = 300;
const MAX_WORKING_MEMORY_ANCHORS = 400;
const COLLABORATION_MODES: readonly CollaborationMode[] = ['ASSISTED', 'COLLABORATIVE', 'AUTONOMOUS'];

const toPositiveInt = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.floor(value);
  }
  return fallback;
};

export const normalizeCollaborationMode = (value: unknown): CollaborationMode | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return COLLABORATION_MODES.includes(normalized as CollaborationMode)
    ? (normalized as CollaborationMode)
    : null;
};

const coerceCollaborationMode = (value: unknown): CollaborationMode | undefined =>
  normalizeCollaborationMode(value) ?? undefined;

const coerceMessages = (value: unknown): Message[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value as Message[];
};

const getMessageContentText = (message: unknown): string => {
  if (!isRecord(message)) {
    return '';
  }
  return messageContentToText(message.content);
};

const buildLastMessageSnippetFromMessages = (
  messages: Message[],
  fallback?: unknown
): string | null => {
  const fallbackSnippet = normalizeSnippetText(fallback);
  if (fallbackSnippet) {
    return fallbackSnippet;
  }

  const lastMessage = messages[messages.length - 1];
  if (!lastMessage) {
    return null;
  }
  const messageText = getMessageContentText(lastMessage);
  return normalizeSnippetText(messageText);
};

const buildContextSummaryFromWorkingMemory = (
  workingMemory: { anchors?: ContextAnchor[] } | null | undefined
): ChatSessionContextAnchor[] => {
  const anchors = Array.isArray(workingMemory?.anchors)
    ? workingMemory.anchors.filter(isContextAnchor)
    : [];

  return anchors
    .filter((anchor) => anchor.entityId.trim().length > 0)
    .sort((left, right) => right.mass - left.mass)
    .slice(0, MAX_CONTEXT_SUMMARY_SIZE)
    .map((anchor) => ({
      entityType: anchor.entityType,
      entityId: anchor.entityId,
      name: anchor.entityId,
      mass: toPositiveInt(anchor.mass, 0),
    }));
};

const coerceContextSummary = (value: unknown): ChatSessionContextAnchor[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const parsed = value
    .filter(isRecord)
    .map((entry) => {
      const entityType = typeof entry.entityType === 'string' ? entry.entityType : null;
      const entityId = typeof entry.entityId === 'string' ? entry.entityId : null;
      const name = typeof entry.name === 'string' && entry.name.trim().length > 0
        ? entry.name.trim()
        : entityId;

      if (!entityType || !entityId || !name) {
        return null;
      }

      return {
        entityType: entityType as ChatSessionContextAnchor['entityType'],
        entityId,
        name,
        mass: toPositiveInt(entry.mass, 0),
      } satisfies ChatSessionContextAnchor;
    })
    .filter((entry): entry is ChatSessionContextAnchor => entry !== null)
    .slice(0, MAX_CONTEXT_SUMMARY_SIZE);

  return parsed.length > 0 ? parsed : undefined;
};

const coerceWorkingMemory = (
  value: unknown,
): WorkingMemory | undefined => {
  if (!isRecord(value)) return undefined;
  const rawAnchors = Array.isArray(value.anchors) ? value.anchors : [];
  const anchors = rawAnchors
    .filter(isRecord)
    .map((entry) => {
      const entityType =
        typeof entry.entityType === 'string' && entry.entityType.trim().length > 0
          ? entry.entityType.trim()
          : null;
      const entityId =
        typeof entry.entityId === 'string' && entry.entityId.trim().length > 0
          ? entry.entityId.trim()
          : null;
      if (!entityType || !entityId) return null;

      const massRaw = typeof entry.mass === 'number' && Number.isFinite(entry.mass)
        ? Math.floor(entry.mass)
        : 1;
      const mass = Math.max(1, massRaw);
      const provenance =
        typeof entry.provenance === 'string' && entry.provenance.trim().length > 0
          ? entry.provenance.trim()
          : undefined;
      const lastAccessedAtRaw =
        typeof entry.lastAccessedAt === 'number' && Number.isFinite(entry.lastAccessedAt)
          ? Math.floor(entry.lastAccessedAt)
          : Date.now();

      const anchor: ContextAnchor = {
        entityType: entityType as ContextAnchor['entityType'],
        entityId,
        mass,
        lastAccessedAt: lastAccessedAtRaw,
      };
      if (provenance) {
        anchor.provenance = provenance;
      }
      if (entry.ephemeralData !== undefined) {
        anchor.ephemeralData = entry.ephemeralData;
      }
      return anchor;
    })
    .filter((entry): entry is ContextAnchor => entry !== null)
    .slice(0, MAX_WORKING_MEMORY_ANCHORS);

  // Master-launched studio sessions carry the originating master session id, preserve it
  // (dropping it here silently broke the "Open on Master" link).
  const originMasterSessionId =
    typeof value.originMasterSessionId === 'string' && value.originMasterSessionId.trim().length > 0
      ? value.originMasterSessionId.trim()
      : undefined;
  const listCreativeContext = readListCreativeContext(value.listCreativeContext);
  // A key this function does not validate is carried as is: whose value is written is decided by
  // preserveServerOwnedMemory, which reads those keys from the stored row, never from the client.
  const {
    anchors: _rawAnchors,
    originMasterSessionId: _rawOriginMasterSessionId,
    listCreativeContext: _rawListCreativeContext,
    ...unvalidated
  } = value;
  return { ...unvalidated, anchors, ...(originMasterSessionId ? { originMasterSessionId } : {}), ...(listCreativeContext ? { listCreativeContext } : {}) };
};

const coerceActiveSubject = (value: unknown): ActiveEntityFocus | null | undefined => {
  if (value === null) return null;
  return normalizeSubjectFocus(value) ?? undefined;
};

const coerceFocusedAt = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return fallback;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return fallback;
  }
  return parsed.toISOString();
};

const coerceSubjectHistory = (
  value: unknown,
  fallbackTimestamp: string
): ChatSubjectFocusEntry[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const parsed: ChatSubjectFocusEntry[] = [];
  value.forEach((entry) => {
    if (!isRecord(entry)) return;
    const activeSubject = coerceActiveSubject(entry);
    if (!activeSubject) return;
    parsed.push(toSubjectFocusHistoryEntry(activeSubject, coerceFocusedAt(entry.focusedAt, fallbackTimestamp)));
  });

  const bounded = parsed.slice(-MAX_SUBJECT_HISTORY_SIZE);
  return bounded.length > 0 ? bounded : undefined;
};

const normalizeDealerChatSession = (
  input: unknown,
  fallback: {
    id: string;
    name: string;
    ownerId: string | null;
    createdAt?: string;
    updatedAt?: string;
  }
): DealerChatSessionState => {
  const source = isRecord(input) ? input : {};
  const {
    activeLeasingAction: _ignoredLegacyActiveSubject,
    projectId: _ignoredLegacyProjectId,
    type: _ignoredLegacySessionType,
    collaborationMode: _ignoredCollaborationMode,
    ...sourceWithoutLegacyField
  } =
    source as Record<string, unknown>;

  const rawName = typeof source.name === 'string' ? source.name.trim() : '';
  const name = rawName.length > 0 ? rawName : fallback.name;

  const createdAt =
    typeof source.createdAt === 'string' && source.createdAt.trim().length > 0
      ? source.createdAt
      : fallback.createdAt ?? toIsoString();

  const updatedAt =
    typeof source.updatedAt === 'string' && source.updatedAt.trim().length > 0
      ? source.updatedAt
      : fallback.updatedAt ?? createdAt;

  const ownerIdRaw = (source as { ownerId?: unknown }).ownerId;
  const ownerId =
    typeof ownerIdRaw === 'string'
      ? ownerIdRaw
      : ownerIdRaw === null
        ? null
        : fallback.ownerId;

  const strategicPlanRaw = (source as { strategicPlan?: unknown }).strategicPlan;
  const strategicPlan =
    typeof strategicPlanRaw === 'string' ? strategicPlanRaw : null;
  const collaborationMode = coerceCollaborationMode(
    (source as { collaborationMode?: unknown }).collaborationMode
  );

  const messagesRaw = (source as { messages?: unknown }).messages;
  const messages = coerceMessages(messagesRaw);
  const messageCount = toPositiveInt((source as { messageCount?: unknown }).messageCount, messages.length);
  const activeMissionCount = toPositiveInt(
    (source as { activeMissionCount?: unknown }).activeMissionCount,
    0
  );
  const lastMessageSnippet = buildLastMessageSnippetFromMessages(
    messages,
    (source as { lastMessageSnippet?: unknown }).lastMessageSnippet
  );
  const contextSummary =
    coerceContextSummary((source as { contextSummary?: unknown }).contextSummary) ??
    buildContextSummaryFromWorkingMemory((source as { workingMemory?: { anchors?: ContextAnchor[] } }).workingMemory);
  const workingMemory = coerceWorkingMemory((source as { workingMemory?: unknown }).workingMemory);
  const parsedActiveSubject = coerceActiveSubject((source as { activeSubject?: unknown }).activeSubject);
  const parsedSubjectHistory = coerceSubjectHistory(
    (source as { subjectHistory?: unknown }).subjectHistory,
    updatedAt
  );
  const { activeSubject, subjectHistory } = migrateLegacySessionProjectId({
    activeSubject: parsedActiveSubject,
    subjectHistory: parsedSubjectHistory,
    projectId: (source as { projectId?: unknown }).projectId,
    updatedAt,
  });

  return {
    ...sourceWithoutLegacyField,
    id: fallback.id,
    name,
    ownerId: ownerId ?? null,
    messageCount,
    activeMissionCount,
    contextSummary,
    workingMemory,
    activeSubject,
    subjectHistory,
    lastMessageSnippet,
    strategicPlan,
    ...(collaborationMode ? { collaborationMode } : {}),
    createdAt,
    updatedAt,
  } as DealerChatSessionState;
};

export const prepareDealerChatSessionForWrite = (
  input: unknown,
  context: {
    id: string;
    name: string;
    ownerId: string | null;
  },
  timestampOverride?: string
): DealerChatSessionState => {
  const updatedAt = timestampOverride ?? toIsoString();
  const normalized = normalizeDealerChatSession(input, {
    id: context.id,
    name: context.name,
    ownerId: context.ownerId,
    updatedAt,
  });

  normalized.name = normalized.name?.trim().length > 0 ? normalized.name : context.name;
  normalized.ownerId = context.ownerId;
  normalized.updatedAt = updatedAt;
  normalized.messageCount = toPositiveInt(normalized.messageCount, 0);
  normalized.lastMessageSnippet = normalizeSnippetText(normalized.lastMessageSnippet) ?? null;

  if (!normalized.contextSummary || normalized.contextSummary.length === 0) {
    const derivedSummary = buildContextSummaryFromWorkingMemory(normalized.workingMemory);
    normalized.contextSummary = derivedSummary.length > 0 ? derivedSummary : undefined;
  }

  const normalizedHistory = Array.isArray(normalized.subjectHistory)
    ? normalized.subjectHistory.slice(-MAX_SUBJECT_HISTORY_SIZE)
    : [];
  if (normalized.activeSubject && normalizedHistory.length === 0) {
    const key = buildSubjectFocusHistoryKey(normalized.activeSubject);
    if (!key) {
      normalized.subjectHistory =
        normalizedHistory.length > 0
          ? normalizedHistory.slice(-MAX_SUBJECT_HISTORY_SIZE)
          : undefined;
      if (!normalized.createdAt || normalized.createdAt.trim().length === 0) {
        normalized.createdAt = updatedAt;
      }
      return normalized;
    }
    normalizedHistory.push(toSubjectFocusHistoryEntry(normalized.activeSubject, normalized.updatedAt));
  }
  normalized.subjectHistory =
    normalizedHistory.length > 0
      ? normalizedHistory.slice(-MAX_SUBJECT_HISTORY_SIZE)
      : undefined;

  if (!normalized.createdAt || normalized.createdAt.trim().length === 0) {
    normalized.createdAt = updatedAt;
  }

  return normalized;
};
